import { FC, useEffect, useState } from 'react';
import AppLayout from '../../components/layout/app-layout/app-layout';
import BtButton from '../bt-button/bt-button';
import { ServiceDownload } from '../../services/service-download';
import { NotificationManager } from "react-notifications";
import { useTranslation } from 'react-i18next';
import {ServiceTranslation} from '../../services/translationService';
import { ServiceProjects } from '../../services/service-projects';
import Modal from '../../components/shared/Modal/Modal';

const PageReports: FC = () => {
  const [t] = useTranslation('reports');

  const [loadingRecommendedSuppliers, setLoadingRecommendedSuppliers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingTranslationFilesStage, setLoadingTranslationFilesStage] = useState('');
  const [translationVersion, setTranslationVersion] = useState(-1);
  const [clearingAllNotifications, setClearingAllNotifications] = useState(false);
  const [showClearNotificationsModal, setShowClearNotificationsModal] = useState(false);

  const translationFileUpdated = t('button.translation.stage.updated', 'uppdaterad, kommer att finnas på alla servrar om en minut');

  useEffect(() => {
    (async () => {
      const translationVersion = await ServiceTranslation.getLatestVersionNumber();
      setTranslationVersion(translationVersion);
    })()
  }, []);

  const downloadReport = async (report: string, setLoading: (value: boolean) => void) => {
    setLoading(true);
    const now = new Date().toISOString().replace("T", "_").replace(/[.].*/, "");
    try {
      await ServiceDownload.downloadUrl(`/api/reports/${report}`, `${report}-${now}.csv`, "text/csv");
    } catch (e) {
      NotificationManager.error("" + e, t('notification.error', 'Något gick fel'));
    } finally {
      setLoading(false);
    }
  }
  const downloadRecommendedSuppliersReport = async () => {
    return await downloadReport("recommended-suppliers", setLoadingRecommendedSuppliers);
  }
  const downloadUsersReport = async () => {
    return await downloadReport("users", setLoadingUsers);
  }
  const downloadContractsReport = async () => {
    return await downloadReport("contracts", setLoadingContracts);
  }
  const downloadProjectsReport = async () => {
    return await downloadReport("projects", setLoadingProjects);
  }
  const downloadSuppliersReport = async () => {
    return await downloadReport("suppliers", setLoadingSuppliers);
  }

  // Maximum 100 seconds + initial delay, should be enough to get the translation files. If not something went wrong.
  const getTranslationVersion = async (processId: string, retries = 100) => {
    if (retries <= 0) {
      const status = await ServiceTranslation.getProcessStatus(processId);
      setLoadingTranslationFilesStage(`status: ${status.status}, message: ${status.message}, ${t('button.translation.stage.error', 'för många försök att hämta översättningsfiler, för att få mer information använd processId')}: ${processId}`);
      return;
    }
    const newTranslationVersion = await ServiceTranslation.getLatestVersionNumber();

    if (newTranslationVersion !== translationVersion) {
      setTranslationVersion(newTranslationVersion);
      setLoadingTranslationFilesStage(translationFileUpdated);
      setTimeout(async () => {
        setLoadingTranslationFilesStage('');
      }, 5000);
      return;
    }

    setTimeout(async () => {
      await getTranslationVersion(processId, retries - 1);
    }, 1000);
  }

  const updateTranslationFiles = async () => {
    setLoadingTranslationFilesStage(t('button.translation.stage.queueing', 'i kö'));
    const processId = await ServiceTranslation.downloadTranslationFiles();
    setLoadingTranslationFilesStage(t('button.translation.stage.queued', 'i kö, väntar på att Lokalise ska behandla'));
    setTimeout(async () => {
      await getTranslationVersion(processId);
    }, 20000); // Initial polling delay, no need to poll too early.
  }

  const handleClearAllNotifications = async () => {
    setClearingAllNotifications(true);
    try {
      const result = await ServiceProjects.clearAllNotificationsFromAllOrganizations();
      
      if (result.count === 0) {
        NotificationManager.info(t('clearNotifications.noNotifications', 'Det finns inga aviseringar att rensa'));
      } else {
        NotificationManager.success(t('clearNotifications.success', 'Alla aviseringar har rensats'));
      }
      
      setShowClearNotificationsModal(false);
    } catch (e) {
      NotificationManager.error("" + e, t('notification.error', 'Något gick fel'));
    } finally {
      setClearingAllNotifications(false);
    }
  }

  const isNotSeparateInstance = window.location.hostname === 'localhost' || window.location.hostname === 'test.accurator.se' || window.location.hostname === 'app.accurator.se';

  return <AppLayout title={t('pageTitle', 'Rapporter')}>
    <div style={{margin:"32px"}}>
      <BtButton onClick={downloadRecommendedSuppliersReport} loaderShow={loadingRecommendedSuppliers} color="dark">{t('button.recommendedSuppliers', 'Rekommenderade leverantörer')}</BtButton>
    </div>
    <div style={{margin:"32px"}}>
      <BtButton onClick={downloadUsersReport} loaderShow={loadingUsers} color="dark">{t('button.users', 'Användare')}</BtButton>
    </div>
    <div style={{margin:"32px"}}>
      <BtButton onClick={downloadContractsReport} loaderShow={loadingContracts} color="dark">{t('button.contracts', 'Kontrakt')}</BtButton>
    </div>
    <div style={{margin:"32px"}}>
      <BtButton onClick={downloadProjectsReport} loaderShow={loadingProjects} color="dark">{t('button.projects', 'Projekt')}</BtButton>
    </div>
    <div style={{margin:"32px"}}>
      <BtButton onClick={downloadSuppliersReport} loaderShow={loadingSuppliers} color="dark">{t('button.suppliers', 'Leverantör')}</BtButton>
    </div>
    {isNotSeparateInstance && <div style={{margin:"32px", display: 'flex', gap: '8px', alignItems: 'center'}}>
      <BtButton onClick={updateTranslationFiles} loaderShow={loadingTranslationFilesStage !== '' && loadingTranslationFilesStage !== translationFileUpdated} color="dark">{t('button.translation.text', 'Uppdatera översättningsfile')}</BtButton>
      <span style={{ fontSize: '14px' }}>{t('button.translation.currentVersion', 'Nuvarande version:')} {translationVersion}</span>
      {loadingTranslationFilesStage !== '' && <span style={{ fontSize: '14px' }}>| {loadingTranslationFilesStage}</span>}
    </div>}
    <div style={{margin:"32px"}}>
      <BtButton onClick={() => setShowClearNotificationsModal(true)} color="dark">{t('button.clearNotifications', 'Rensa alla aviseringar')}</BtButton>
    </div>
    
    <Modal
      title={t('clearNotifications.modal.title', 'Rensa alla aviseringar')}
      show={showClearNotificationsModal}
      setShow={setShowClearNotificationsModal}
      buttonInfo={{
        label: t('clearNotifications.modal.confirm', 'Rensa'),
        loaderShow: clearingAllNotifications,
        action: handleClearAllNotifications,
      }}
    >
      <span>{t('clearNotifications.modal.message', 'Är du säker på att du vill rensa alla aviseringar från alla projekt? Detta kan inte ångras.')}</span>
    </Modal>
  </AppLayout>
}

export default PageReports;
